// services/api.ts

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';

// Base API URL - dynamically use current hostname for local dev (allows mobile testing)
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // Use current hostname with API port for local development
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:8080`;
};
const API_BASE_URL = getApiBaseUrl();

/** Axios Client for regular API calls */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000, // 3 minutes - matches nginx proxy_read_timeout for AI operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Inject auth token for every axios request
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Recursively normalize localhost URLs in response data (for mobile testing)
const normalizeResponseUrls = (data: unknown): unknown => {
  if (typeof data === 'string' && data.includes('localhost:8080')) {
    return data.replace(/http:\/\/localhost:8080/g, `${window.location.protocol}//${window.location.hostname}:8080`);
  }
  if (Array.isArray(data)) {
    return data.map(normalizeResponseUrls);
  }
  // Only plain JSON objects: rebuilding a Blob/ArrayBuffer (file downloads)
  // from its enumerable keys would turn it into an empty {}.
  if (data && typeof data === 'object' && Object.getPrototypeOf(data) === Object.prototype) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      result[key] = normalizeResponseUrls(value);
    }
    return result;
  }
  return data;
};

// Track whether a token refresh is already in progress to avoid concurrent refresh loops
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Credits are held server-side the moment a paid request is accepted, so the balance
// shown in the header refreshes after every successful paid call — no screen has to
// remember to do it. Debounced so a burst of calls triggers one refresh.
let creditsRefreshTimer: ReturnType<typeof setTimeout> | undefined;
export const scheduleCreditsRefresh = () => {
  clearTimeout(creditsRefreshTimer);
  creditsRefreshTimer = setTimeout(() => window.dispatchEvent(new CustomEvent('creditsUpdated')), 300);
};
const PAID_REQUEST = /\/api\/v1\/(vibe-templates|media|campaigns|conversations)\//;

// Handle responses - normalize URLs and handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => {
    response.data = normalizeResponseUrls(response.data);
    const method = (response.config.method || 'get').toLowerCase();
    if (method !== 'get' && PAID_REQUEST.test(response.config.url || '')) {
      scheduleCreditsRefresh();
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean; _networkRetry?: number };

    // ── Auto-retry transient network errors (ERR_NETWORK, ERR_CONNECTION_RESET) ──
    // These happen when multiple components fire API calls simultaneously on mount
    // and the Docker gateway briefly drops connections.
    const isNetworkError = !error.response && error.code && ['ERR_NETWORK', 'ECONNABORTED', 'ERR_CONNECTION_RESET'].includes(error.code);
    const retryCount = originalRequest._networkRetry ?? 0;
    if (isNetworkError && retryCount < 2 && originalRequest) {
      originalRequest._networkRetry = retryCount + 1;
      await new Promise(r => setTimeout(r, 300 * (retryCount + 1))); // 300ms, 600ms
      return apiClient(originalRequest);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if we're already on the refresh or login endpoint
      const url = originalRequest.url || '';
      if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
        console.warn('401 on auth endpoint — clearing session');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('refresh_token');
        return Promise.reject(error);
      }

      const refreshToken =
        localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

      if (!refreshToken) {
        console.warn('401 Unauthorized — no refresh token available');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request until the in-progress refresh completes
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            if (originalRequest.headers) {
              (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/api/v1/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const { access_token: newAccessToken, refresh_token: newRefreshToken } = refreshResponse.data;

        // Persist new tokens in the same storage that held the originals
        const inLocal = !!localStorage.getItem('refresh_token');
        if (inLocal) {
          localStorage.setItem('auth_token', newAccessToken);
          if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
        } else {
          sessionStorage.setItem('auth_token', newAccessToken);
          if (newRefreshToken) sessionStorage.setItem('refresh_token', newRefreshToken);
        }

        onRefreshed(newAccessToken);
        isRefreshing = false;

        // Retry the original request with the new access token
        if (originalRequest.headers) {
          (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newAccessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        console.error('Token refresh failed — clearing session');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('refresh_token');
        return Promise.reject(refreshError);
      }
    }

    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 401: console.warn('401 Unauthorized:', error.config?.url); break;
        case 403: console.error('Access forbidden:', error.response.data); break;
        case 404: console.error('Resource not found:', error.config?.url); break;
        case 429: console.error('Rate limit exceeded'); break;
        case 500: case 502: case 503: case 504: console.error('Server error:', error.response.data); break;
        default: console.error('API error:', error.response.data);
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

/** Auth helpers */
export const setAuthToken = (token: string, remember = false) => {
  if (remember)
    localStorage.setItem('auth_token', token);
  else
    sessionStorage.setItem('auth_token', token);
};

export const clearAuthToken = () => {
  localStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_token');
};

export const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

export const isAuthenticated = (): boolean => !!getAuthToken();

/**
 * Normalize media URLs for local dev - replaces localhost with current hostname
 * so images work on both laptop and mobile
 */
export const normalizeMediaUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.includes('localhost:8080')) {
    return url.replace('http://localhost:8080', `${window.location.protocol}//${window.location.hostname}:8080`);
  }
  return url;
};

/** SSE/LLM Streaming Client */
export async function streamSSE(
  endpoint: string,
  onMessage: (data: string) => void,
  onError?: (err: any) => void
): Promise<void> {
  try {
    const token = getAuthToken();
    const url =
      endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/event-stream',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok || !response.body) {
      throw new Error(`Network response was not ok`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let lines = buffer.split('\n');
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const message = line.slice(6).trim();
          if (message !== '') onMessage(message);
        }
      }
    }
  } catch (err) {
    if (onError) onError(err);
    else console.error('SSE error:', err);
  }
}

export default apiClient;
